import {
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
} from '@tanstack/react-query';
import mapApiObjectToCamelCase, {
  ApiObject,
} from '~/libs/api-hooks/architect/utils/mapApiObjectToCamelCase';
import requester, {
  UrlOptions,
} from '~/libs/api-hooks/architect/utils/requester';
import StatusError from '~/libs/api-hooks/architect/utils/statusError';
import { QueryMap } from '~/libs/api-hooks/architect/utils/prepareSearchParams';
import DeepPartial from '~/libs/api-hooks/architect/utils/DeepPartial';
import ExceptionLogger from '~/libs/api-hooks/architect/backplane/makeExceptionLogger';

export interface fetchReturn<DeepPartialItem> {
  items: DeepPartialItem[];
  pageParam: number;
}

export type PaginatedApiOptions<
  Item,
  DeepPartialItem extends DeepPartial<Item> = DeepPartial<Item>,
> = Omit<
  UseInfiniteQueryOptions<fetchReturn<DeepPartialItem>, StatusError>,
  'queryKey' | 'queryFn' | 'getNextPageParam' | 'getPreviousPageParam'
>;

export type PaginatedApiResult<
  Item,
  DeepPartialItem extends DeepPartial<Item> = DeepPartial<Item>,
> = UseInfiniteQueryResult<fetchReturn<DeepPartialItem>, StatusError>;

export interface UsePaginatedApiProps<
  Item,
  DeepPartialItem extends DeepPartial<Item> = DeepPartial<Item>,
> {
  pathname: string;
  cacheKey: string[];
  queryMap?: QueryMap;
  page?: number;
  reactQueryOptions?: PaginatedApiOptions<Item, DeepPartialItem>;
  urlOptions?: UrlOptions;
  includeApiKeyWhenAvailable?: boolean;
  customHeaders?: Record<string, string>;
}

export const defaultPageSize = 100;

const usePaginatedApi = <
  Item,
  DeepPartialItem extends DeepPartial<Item> = DeepPartial<Item>,
>({
  pathname,
  cacheKey,
  queryMap,
  page = 1,
  reactQueryOptions = {},
  urlOptions,
  includeApiKeyWhenAvailable = true,
  customHeaders,
}: UsePaginatedApiProps<Item, DeepPartialItem>): PaginatedApiResult<
  Item,
  DeepPartialItem
> => {
  const getNextPageParam = (lastPage: fetchReturn<DeepPartialItem>) => {
    return lastPage?.items?.length === defaultPageSize
      ? lastPage.pageParam + 1
      : undefined;
  };

  const getPreviousPageParam = (firstPage: fetchReturn<DeepPartialItem>) => {
    return firstPage.pageParam === 1 ? undefined : firstPage.pageParam - 1;
  };

  const fetchAPI = async ({
    pageParam = page,
  }): Promise<fetchReturn<DeepPartialItem>> => {
    queryMap = {
      ...queryMap,
      page: pageParam.toString(),
      'per-page': defaultPageSize.toString(),
    };
    const response = await requester({
      pathname,
      queryMap,
      method: 'GET',
      urlOptions,
      includeApiKeyWhenAvailable,
      sendCsrfToken: false,
      customHeaders,
    });

    try {
      const responseJson = await response.json();
      if (!Array.isArray(responseJson)) {
        throw new Error('usePaginatedApi expects an array to be returned');
      }
      const responseParsed = responseJson.map((repo: ApiObject) =>
        mapApiObjectToCamelCase<DeepPartialItem>(repo),
      );

      return {
        items: responseParsed,
        pageParam,
      };
    } catch (error: any) {
      ExceptionLogger().error(error.message, { error });
      throw new StatusError({
        message: error.message,
        status: 0,
        pathname,
      });
    }
  };

  return useInfiniteQuery<fetchReturn<DeepPartialItem>, StatusError>(
    cacheKey,
    fetchAPI,
    {
      getNextPageParam,
      getPreviousPageParam,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
      ...reactQueryOptions,
    },
  );
};

export default usePaginatedApi;
